import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogContent,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import SportsHockeyIcon from "@mui/icons-material/SportsHockey";
import { useNavigate, useLocation } from "react-router-dom";
import { fireConfetti } from "../../utils/fireConfetti";
import { getMyLeagues } from "../../services/api/leagues";
import { verifyCheckoutSession, fetchMySubscription } from "../../services/api/subscriptions";
import { hasLocallyValidSession } from "../../services/auth/auth-session";
import { PLAN_DEFINITIONS } from "../../config/plans";
import QrCodeDialog from "../../components/QrCodeDialog";
import ParticipantListDialog from "../../components/ParticipantListDialog";

const ORGANIZER_ROLES = new Set(["owner", "admin", "treasurer", "coach"]);

const SKILL_LEVEL_LABELS = {
  A: "A",
  A_B: "A/B",
  B: "B",
  B_C: "B/C",
  C_BEGINNER: "C / Beginner",
  ALL_WELCOME: "All welcome",
};

export default function MyLeaguesView() {
  const navigate = useNavigate();
  const location = useLocation();
  const [leagues, setLeagues] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [qrLeague, setQrLeague] = React.useState(null);
  const [participantsLeague, setParticipantsLeague] = React.useState(null);
  const [welcomeOpen, setWelcomeOpen] = React.useState(false);
  const [welcomePlan, setWelcomePlan] = React.useState(null);
  const [subscription, setSubscription] = React.useState(null);


  React.useEffect(() => {
    if (!hasLocallyValidSession()) {
      navigate("/pricing", { replace: true });
      return;
    }
    Promise.allSettled([getMyLeagues(), fetchMySubscription()])
      .then(([leaguesResult, subResult]) => {
        if (leaguesResult.status === "fulfilled") {
          setLeagues(Array.isArray(leaguesResult.value) ? leaguesResult.value : []);
        } else {
          const msg = leaguesResult.reason?.message || "";
          if (!msg.includes("Authentication required")) {
            setError("Something went wrong loading your leagues. Please refresh and try again.");
          }
        }
        const sub = subResult.status === "fulfilled" ? subResult.value : null;
        if (!sub) {
          navigate("/pricing", { replace: true });
          return;
        }
        setSubscription(sub);
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isCheckoutSuccess = params.get("checkout") === "success";
    const sessionId = params.get("session_id");

    if (!isCheckoutSuccess || !sessionId) return;

    // Strip the query params from the URL immediately so a refresh doesn't re-trigger
    window.history.replaceState({}, "", location.pathname);

    verifyCheckoutSession(sessionId)
      .then((result) => {
        if (!result?.paid) return;
        const plan = PLAN_DEFINITIONS.find((p) => p.id === result.planId) ?? null;
        setWelcomePlan(plan);
        setWelcomeOpen(true);
        fireConfetti();
      })
      .catch(() => {
        // Silently ignore — don't block the page if verification fails
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const leagueTypeLabel = (type) => (type === "COMPETITIVE" ? "Competitive" : "Pickup");
  const skillLevelLabel = (level) => SKILL_LEVEL_LABELS[level] ?? level ?? "—";

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {qrLeague && (
        <QrCodeDialog
          open={!!qrLeague}
          onClose={() => setQrLeague(null)}
          title={qrLeague.name}
          subtitle="Scan to join this league"
          url={`${window.location.origin}/leagues/${qrLeague.id}/signup`}
        />
      )}

      <ParticipantListDialog
        open={!!participantsLeague}
        onClose={() => setParticipantsLeague(null)}
        leagueId={participantsLeague?.id}
        leagueName={participantsLeague?.name}
      />

      <Dialog open={welcomeOpen} maxWidth="xs" fullWidth>
        <DialogContent>
          <Stack spacing={3} alignItems="center" sx={{ py: 2, textAlign: "center" }}>
            <SportsHockeyIcon sx={{ fontSize: 56, color: "#C8102E" }} />
            <Typography variant="h5" fontWeight={700}>
              You&apos;re in!
            </Typography>
            {welcomePlan && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: -1.5 }}>
                You&apos;re now on the <strong>{welcomePlan.name}</strong> plan
                {welcomePlan.monthlyPrice === 0 ? " — free, forever." : "."}
              </Typography>
            )}
            <Typography variant="body1" color="text.secondary">
              Lace &apos;em up — your Puck-Pickup league is ready to drop the puck.
              Set up your first league and get your crew on the schedule.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
              No penalties for beginners. We promise.
            </Typography>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={() => { setWelcomeOpen(false); navigate("/new-league"); }}
              sx={{ mt: 1, fontWeight: 700, fontSize: "1.05rem" }}
            >
              Let&apos;s Go!
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" component="h3">
              My leagues
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Leagues you run or participate in.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {leagues.some((l) => ORGANIZER_ROLES.has(l.userRole)) && (
              <Button variant="outlined" onClick={() => navigate("/create-game")}>
                Create Game
              </Button>
            )}
            {(() => {
              const isLaunchTier = !subscription || subscription.planId === "launch";
              const hasOrganizerLeague = leagues.some((l) => ORGANIZER_ROLES.has(l.userRole));
              const atLeagueLimit = isLaunchTier && hasOrganizerLeague;
              return (
                <Tooltip
                  title={atLeagueLimit ? "Upgrade your plan to create additional leagues." : ""}
                  disableHoverListener={!atLeagueLimit}
                >
                  <span>
                    <Button
                      variant="contained"
                      disabled={atLeagueLimit}
                      onClick={() => navigate("/new-league")}
                    >
                      New League
                    </Button>
                  </span>
                </Tooltip>
              );
            })()}
          </Stack>
        </Stack>

        {error ? (
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Typography color="text.secondary">Loading…</Typography>
        ) : leagues.length === 0 && !error ? (
          <Box textAlign="center" sx={{ py: 8 }}>
            <SportsHockeyIcon sx={{ fontSize: 52, color: "text.disabled", mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No leagues yet
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Create your first league to get your crew on the ice.
            </Typography>
            <Button variant="contained" onClick={() => navigate("/new-league")}>
              Create a League
            </Button>
          </Box>
        ) : (
          <Stack spacing={2}>
            {leagues.map((league) => (
              <Card key={league.id} variant="outlined">
                <CardContent>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ sm: "center" }}
                    gap={2}
                  >
                    <Box
                      sx={{ cursor: ORGANIZER_ROLES.has(league.userRole) ? "pointer" : "default", flex: 1 }}
                      onClick={() => {
                        if (ORGANIZER_ROLES.has(league.userRole)) navigate(`/manage-league/${league.id}`);
                      }}
                    >
                      <Typography variant="h6">{league.name}</Typography>
                      {(league.city || league.state) && (
                        <Typography variant="body2" color="text.secondary">
                          {[league.city, league.state].filter(Boolean).join(", ")}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {leagueTypeLabel(league.leagueType)}
                        {" · "}
                        {skillLevelLabel(league.skillLevel)}
                        {league.plan?.displayName ? ` · ${league.plan.displayName}` : ""}
                        {league.uniquePlayerCount != null
                          ? ` · ${league.uniquePlayerCount} player${league.uniquePlayerCount !== 1 ? "s" : ""}`
                          : ""}
                        {league.gameRegistrationCount != null
                          ? ` · ${league.gameRegistrationCount} game registration${league.gameRegistrationCount !== 1 ? "s" : ""}`
                          : ""}
                      </Typography>
                    </Box>
                    <Stack
                      direction="row"
                      spacing={1}
                      flexShrink={0}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<QrCode2Icon />}
                        onClick={() => setQrLeague(league)}
                        sx={{ flexBasis: { xs: "calc(50% - 4px)", sm: "auto" } }}
                      >
                        Share signup
                      </Button>
                      {ORGANIZER_ROLES.has(league.userRole) && (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setParticipantsLeague(league)}
                            sx={{ flexBasis: { xs: "calc(50% - 4px)", sm: "auto" } }}
                          >
                            Participants
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              navigate("/create-game", { state: { leagueId: league.id } })
                            }
                            sx={{ flexBasis: { xs: "calc(50% - 4px)", sm: "auto" } }}
                          >
                            + Game
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => navigate(`/manage-league/${league.id}`)}
                            sx={{ flexBasis: { xs: "calc(50% - 4px)", sm: "auto" } }}
                          >
                            Manage
                          </Button>
                        </>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>
    </Container>
  );
}
